/**
 * Clés i18n construites dynamiquement par le monitoring.
 *
 * Les libellés d'énumérations, de métriques et de colonnes sont des clés
 * calculées (`monitoring.enum.<nom>.<code>`, `metric.<ID>.label`…) : un
 * `grep` des appels à `t('…')` ne les voit pas. Elles sont donc déclarées ici,
 * une fois, et le test d'exhaustivité vérifie leur présence en fr, en et ar.
 *
 * Provenance des codes : CONTRAT_API_V2 §10.4 (v2.1), sauf mention.
 * `actor_kind.total` n'est pas un code de l'énumération : c'est le libellé de
 * la ligne « total » de la ventilation IA-07.
 */
import { useTranslation } from 'react-i18next';
import {
    AI_PRICE_FIELDS,
    AI_SOURCES,
    COST_STATUSES,
    API_KINDS,
    EMPTY_REASONS,
    STUDENT_STATUSES,
    TRACE_STATUSES,
} from '../../../api/monitoring/v2';
import { PRESET_KEYS } from './periods';
import { SERIES_REGISTRY } from './seriesRegistry';

export const ENUMS: Record<string, readonly string[]> = {
    // [contrat]
    student_status: STUDENT_STATUSES,
    subscription_state: ['active', 'expiring_soon', 'expired', 'awaiting_first_payment', 'upcoming', 'never'],
    renewal: ['yes', 'no', 'unknown'],
    ai_source: AI_SOURCES,
    ai_status: TRACE_STATUSES,
    // [contrat §10.4] — valorisation d'un appel ; aussi les clés de `cost_status_counts`
    cost_status: COST_STATUSES,
    // [contrat, golden lot 8] — colonne `amount_confirmed` des paiements
    amount_confirmed: ['yes', 'no', 'unknown'],
    actor_kind: ['student', 'staff', 'bench', 'system', 'total'],
    // [contrat §10.4]
    ai_outcome: ['ok', 'not_applicable', 'repaired_json', 'repaired_semantic', 'failed_validation'],
    // [contrat §10.4 : référentiel ai_operation] — semis de la migration 0093, complété
    ai_operation: [
        'quest_generation_stream',
        'quest_generation',
        'visual_explanation',
        'neutral_hook',
        'json_repair',
        'semantic_repair',
        'redaction_repair',
        'answer_key_review',
        'blind_solve',
        'blind_table',
        'chat_completion',
        'title',
        'rag_plan',
        'rag_embed',
        'agent',
        'reflect_exercise',
        'reflect_illustration',
        'eval',
        'objectives',
        'knowledge_extract',
        'knowledge_review',
        'embedding',
        'stt',
        'tts',
        // Opérations ajoutées au référentiel par les migrations 0122-0141
        // (contrôles et générateur v3 de tc_learn, objectifs, OCR, atelier
        // tc_docsourcing) : sans libellé, l'écran Traces affichait la clé.
        'same_datum_check',
        'error_spotting_check',
        'quest_generation_v3',
        'quest_hook_v3',
        'text_review',
        'objectives_review',
        'objectives_identity',
        'ocr',
        'failure_debrief',
        'course_writing',
        'course_review',
        'course_landmarks',
    ],
    api_kind: API_KINDS,
    // [contrat §10.4]
    invoice_status: ['ouverte', 'payee', 'annulee', 'remboursee', 'offerte'],
    // [contrat §10.4]
    invoice_nature: ['checkout', 'renouvellement', 'siege_ecole', 'echeance_org', 'octroi_admin'],
    debtor: ['famille', 'ecole', 'administration'],
    payment_provider: ['cashplus', 'lemonsqueezy', 'virement', 'legacy'],
    payment_nature: ['encaissement', 'remboursement'],
    paid_at_origin: [
        'prestataire',
        'traitement',
        'reprise_journal',
        'reprise_updated_at',
        'reprise_creation',
        'reprise_arbitrage',
    ],
    access_origin: ['paiement', 'siege_ecole', 'octroi_admin'],
    // [base] — motifs de quest_report (v1)
    report_reason: ['exercise_wrong', 'narration_incoherent', 'visual_wrong', 'other'],
};

/** Identifiants de tuiles : catalogue 6.4, plus les tuiles hors catalogue du contrat. */
export const METRIC_IDS = [
    'PED-01', 'PED-02', 'PED-03', 'PED-04', 'PED-05', 'PED-06', 'PED-07', 'PED-08', 'PED-09', 'PED-10', 'PED-11',
    'IA-01', 'IA-02', 'IA-03', 'IA-04', 'IA-05', 'IA-06', 'IA-07',
    'TR-01', 'TR-02',
    'FIN-01', 'FIN-02', 'FIN-03', 'FIN-04', 'FIN-05', 'FIN-06', 'FIN-07', 'FIN-08', 'FIN-09', 'FIN-10', 'FIN-11',
    'FIN-12', 'FIN-13',
    // hors catalogue : octrois (§7), qualité des données (§7), historique non daté (§4), issues (§5)
    'FIN-GRANTS', 'FIN-DQ', 'IA-UNDATED', 'TR-OUTCOME', 'TR-STARTED',
] as const;
export type MetricId = (typeof METRIC_IDS)[number];

/**
 * Clés de colonnes des tableaux listées par le contrat. Le serveur envoie le
 * libellé sous forme de clé i18n ; la convention `monitoring.col.<key>` est
 * celle des deux exemples du contrat (`monitoring.col.name`, `…col.paid`) —
 * à confirmer pour les autres (demande de contrat).
 */
export const COLUMN_KEYS = [
    // exemples du contrat
    'name', 'paid',
    // pédagogie
    'id', 'attached_to', 'status', 'lessons_completed', 'exercises_submitted', 'projects_created', 'sessions',
    'active_minutes', 'progress', 'last_activity', 'subject',
    // IA
    'model', 'actor_kind', 'calls', 'tokens', 'cost', 'cost_status_counts', 'questions', 'contents',
    // finance
    'issued_at', 'nature', 'payer', 'debtor', 'beneficiaries', 'amount', 'paid_at', 'attempts', 'provider',
    'paid_at_origin', 'amount_confirmed', 'invoices', 'student', 'state', 'coverage_end', 'renewal', 'origin',
    'paid_in_period', 'last_payment_at',
    // traces (§5), et colonnes demandées au lot 9-bis
    'call_id', 'started_at', 'source', 'operation', 'outcome', 'learner', 'duration_ms', 'quest_id', 'report_count',
    'currency', 'cost_status',
] as const;

/** Codes d'erreur 400/404 du contrat §1, traduits sous `monitoring.errors.code.<code>`. */
export const ERROR_CODES = [
    'invalid_period',
    'invalid_date',
    'window_too_large',
    'invalid_scope',
    'scope_type_mismatch',
    'invalid_filter',
    'invalid_pagination',
    'scope_not_found',
    'not_found',
] as const;

export const EMPTY_REASON_KEYS = EMPTY_REASONS.map((reason) => `monitoring.empty.${reason}`);

/** Toutes les clés dynamiques attendues dans chaque langue. */
export const dynamicKeys = (): string[] => [
    ...Object.entries(ENUMS).flatMap(([name, codes]) => codes.map((code) => `monitoring.enum.${name}.${code}`)),
    ...METRIC_IDS.flatMap((id) => [`metric.${id}.label`, `metric.${id}.help`]),
    ...COLUMN_KEYS.map((key) => `monitoring.col.${key}`),
    ...EMPTY_REASON_KEYS,
    ...ERROR_CODES.map((code) => `monitoring.errors.code.${code}`),
    ...PRESET_KEYS.map((key) => `monitoring.presets.${key}`),
    ...['past', 'current', 'future'].map((kind) => `monitoring.periodKind.${kind}`),
    ...['input', 'cached', 'output', 'reasoning'].map((k) => `monitoring.tokens.${k}`),
    ...['quest', 'visual', 'exercise', 'illustration', 'reused'].map((k) => `monitoring.contents.${k}`),
    ...AI_PRICE_FIELDS.map((field) => `monitoring.aiPrices.field.${field}`),
    ...['computed', 'partial', 'none'].map((status) => `monitoring.cost.status.${status}`),
    ...Object.values(SERIES_REGISTRY).map((meta) => meta.labelKey),
    'monitoring.scope.all',
    'monitoring.scope.allMine',
    'sidebar.monitoringAiPrices',
];

/**
 * Libellé d'un code d'énumération. Un code inconnu des traductions reste
 * affiché tel quel (jamais masqué ni remplacé) et signalé en console.
 */
export const useEnumLabel = () => {
    const { t, i18n } = useTranslation();
    return (name: string, code: string | null | undefined): string => {
        if (code === null || code === undefined || code === '') return '—';
        const key = `monitoring.enum.${name}.${code}`;
        if (!i18n.exists(key)) {
            // eslint-disable-next-line no-console
            console.warn('[monitoring] libellé d’énumération manquant', key);
            return code;
        }
        return t(key);
    };
};
